import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Heart, Plus, Award, Lock, Trash2 } from 'lucide-react';
import { PublicNav } from '../components/PublicNav';
import { ChatPanel } from '../components/ChatPanel';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

const fileToBase64 = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', body: '', milestone: '', image_base64: '', is_public: true, is_gated: false,
  });

  const load = () => api.get('/community/posts').then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const onImg = async (e) => {
    if (e.target.files?.[0]) {
      setForm({ ...form, image_base64: await fileToBase64(e.target.files[0]) });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/community/posts', form);
      toast.success('Posted');
      setOpen(false);
      setForm({ title: '', body: '', milestone: '', image_base64: '', is_public: true, is_gated: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const like = async (p) => {
    try {
      await api.post(`/community/posts/${p.id}/like`);
      load();
    } catch { toast.error('Sign in to like'); }
  };

  const del = async (p) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/community/posts/${p.id}`);
    load();
  };

  const canPost = user && ['ADMIN', 'TRAINER', 'FRONT_DESK'].includes(user.role);

  return (
    <div className='min-h-screen bg-[#FFF8F2] dark:bg-slate-950'>
      <PublicNav />

      {/* Hero — compact, leaving room for chat above the fold */}
      <section className='relative hero-bg noise overflow-hidden'>
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10'>
          <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Community</div>
          <h1 className='font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 mt-1.5' data-testid='community-heading'>
            Your Dog Community
          </h1>
          <p className='text-slate-700 dark:text-slate-300 mt-2 max-w-2xl text-sm' data-testid='community-subheading'>
            Dog Talk Near You
          </p>
        </div>
      </section>

      {/* Universal Group Chat — pinned at the top */}
      <section className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2'>
        {user ? (
          <ChatPanel
            channel='universal'
            title='Group Chat · The JFK9 Pack'
            subtitle='Open to all members & staff. Admins and Trainers carry verified badges.'
            canPost={true}
            data-testid='universal-chat'
          />
        ) : (
          <Card className='p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3'>
            <div>
              <div className='font-display font-bold text-lg text-[#0B1220] dark:text-slate-50'>Join the conversation</div>
              <p className='text-sm text-slate-600 dark:text-slate-300 mt-1'>
                Sign in to chat with the JFK9 pack — trainers, staff, and members share updates here.
              </p>
            </div>
            <Button
              onClick={() => window.location.href = '/login'}
              className='bg-[#1B3A87] hover:bg-[#16306F] text-white shrink-0'
              data-testid='chat-login-cta'
            >
              Sign in to chat
            </Button>
          </Card>
        )}
      </section>

      {/* Success Timeline (existing feed) */}
      <section className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 mt-2' data-testid='community-feed'>
        <div className='flex items-center justify-between mb-5 flex-wrap gap-3'>
          <div>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Success Timeline</div>
            <h2 className='font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 mt-1'>
              Transformations & Milestones
            </h2>
          </div>
          {canPost && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='create-post-button'>
                  <Plus className='w-4 h-4 mr-2' /> New post
                </Button>
              </DialogTrigger>
              <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
                <DialogHeader><DialogTitle>New Success Timeline post</DialogTitle></DialogHeader>
                <form onSubmit={submit} className='space-y-3'>
                  <div>
                    <Label>Title *</Label>
                    <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid='post-title-input' />
                  </div>
                  <div>
                    <Label>Milestone</Label>
                    <Input value={form.milestone} onChange={(e) => setForm({ ...form, milestone: e.target.value })} placeholder='Basic Obedience Graduate' data-testid='post-milestone-input' />
                  </div>
                  <div>
                    <Label>Body *</Label>
                    <Textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid='post-body-input' />
                  </div>
                  <div>
                    <Label>Image</Label>
                    <Input type='file' accept='image/*' onChange={onImg} data-testid='post-image-input' />
                  </div>
                  <div className='flex items-center gap-2 text-sm'>
                    <label className='flex items-center gap-1'>
                      <input type='checkbox' checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} /> Public
                    </label>
                    <label className='flex items-center gap-1 ml-3'>
                      <input type='checkbox' checked={form.is_gated} onChange={(e) => setForm({ ...form, is_gated: e.target.checked })} /> Members only (gated)
                    </label>
                  </div>
                  <DialogFooter>
                    <Button type='submit' className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='post-submit-button'>Publish</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className='space-y-5'>
          {posts.length === 0 && (
            <Card className='p-10 text-center text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
              No posts yet.
            </Card>
          )}
          {posts.map((p) => (
            <Card
              key={p.id}
              className='overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-md transition-shadow'
              data-testid={`post-card-${p.id}`}
            >
              <div className='flex flex-col md:flex-row'>
                <div className='w-2 md:h-auto bg-[#1B3A87] md:w-1.5' />
                {p.image_base64 && (
                  <img src={p.image_base64} alt={p.title} className='w-full md:w-64 h-48 md:h-auto object-cover' />
                )}
                <div className='flex-1 p-5'>
                  <div className='flex items-center gap-2 mb-2 flex-wrap'>
                    {p.milestone && (
                      <Badge className='bg-[#B08D57]/15 text-[#B08D57] border-0'>
                        <Award className='w-3 h-3 mr-1' /> {p.milestone}
                      </Badge>
                    )}
                    {p.is_gated && (
                      <Badge className='bg-[#C8102E]/10 text-[#C8102E] border-0'>
                        <Lock className='w-3 h-3 mr-1' /> Members
                      </Badge>
                    )}
                    <Badge variant='outline' className='border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs'>
                      {p.author_role}
                    </Badge>
                  </div>
                  <h3 className='font-display text-xl font-bold text-[#0B1220] dark:text-slate-50'>{p.title}</h3>
                  <p className='text-sm text-slate-700 dark:text-slate-200 mt-2 leading-relaxed whitespace-pre-wrap'>{p.body}</p>
                  <div className='flex items-center gap-3 mt-4 text-xs text-slate-500 dark:text-slate-400'>
                    <button
                      className='flex items-center gap-1 hover:text-[#C8102E]'
                      onClick={() => like(p)}
                      data-testid={`like-button-${p.id}`}
                    >
                      <Heart
                        className={`w-4 h-4 ${user && p.liked_by?.includes(user.id) ? 'fill-[#C8102E] text-[#C8102E]' : ''}`}
                      /> {p.likes || 0}
                    </button>
                    <span>·</span>
                    <span>{p.author_name}</span>
                    <span>·</span>
                    <span>{new Date(p.created_at).toLocaleDateString()}</span>
                    {canPost && (
                      <button
                        className='ml-auto text-slate-400 hover:text-[#B42318]'
                        onClick={() => del(p)}
                        data-testid={`delete-post-${p.id}`}
                      >
                        <Trash2 className='w-3.5 h-3.5' />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Floating quick-chat bubble — available site-wide from every public page */}
      <MessageGregBubble />
    </div>
  );
}
